/**
 * v137 — Spine What-If matrix.
 *
 * Tuning-shop diagnostic. When a user's selected spine reads out-of-window,
 * the Build Review and Spine Calculator views surface a "what-if you used
 * a different spine?" panel that cycles through the standard industry
 * spine ladder and shows the verdict for each at the user's current
 * effective setup (everything held constant except the spine value).
 *
 * Reuses the v135.1 canonical algorithm `recommendSpineFromInputs` —
 * one call per spine in the ladder, identical inputs otherwise. That
 * guarantees the matrix verdicts agree with the main verdict and with
 * Spine Review / Build Review at the same inputs.
 *
 * No new physics. No new chart rows. Pure diagnostic surface.
 */

import {
  recommendSpineFromInputs,
  type SpineFromInputs,
} from './spine';
import type { SpineRecommendation } from '@/types';

/**
 * Industry-standard spine ladder used by Easton, Gold Tip, Black Eagle,
 * Carbon Express, Victory, and Beman across hunting + target catalogs.
 *
 * Order: stiffest to weakest (lower number = stiffer). Standardized by
 * the ATA spine-tester (28" suspended, 1.94 lb weight, deflection in
 * thousandths of an inch — 250 spine deflects 0.250", 400 deflects 0.400").
 */
export const SPINE_LADDER = [250, 300, 340, 400, 500, 600, 700, 800, 900, 1000] as const;

export type LadderSpine = (typeof SPINE_LADDER)[number];

/** Inputs to the matrix — same shape as SpineFromInputs minus the spine. */
export type SpineMatrixInputs = Omit<SpineFromInputs, 'selectedSpine'>;

/** One row of the matrix output. */
export interface SpineMatrixRow {
  spine: number;
  /** Verdict for this spine at the user's current setup. */
  status: SpineRecommendation['status']; // 'ok' | 'stiff' | 'weak'
  /**
   * Refined verdict tier matching the in-app chip styling.
   *   on              — selected ∈ [recMin, recMax] (chart range)
   *   slightly-stiff  — selected ∈ [recMin − 25, recMin) (borderline, in tolerance)
   *   slightly-weak   — selected ∈ (recMax, recMax + 25]
   *   too-stiff       — selected < recMin − 25
   *   too-weak        — selected > recMax + 25
   */
  verdict: 'on' | 'slightly-stiff' | 'slightly-weak' | 'too-stiff' | 'too-weak';
  /** Plain-English explanation, mirrored from recommendSpineFromInputs.message. */
  message: string;
  /** The recommended spine range at this setup. Identical across all rows. */
  recommendedMin: number;
  recommendedMax: number;
  /** Effective draw weight at this setup. Identical across all rows. */
  effectiveDrawWeight: number;
}

/**
 * Map a SpineRecommendation to the 5-state verdict ladder used by the UI.
 *
 * Exported so every surface that grades a selected spine (what-if matrix,
 * wizard pre-flight checklist, gauge) derives its verdict from the SAME
 * recommendSpine engine — a heuristic fork here is what caused the
 * heatmap-says-weak / checklist-says-stiff contradiction fixed in v168.
 */
export function spineVerdictForSelection(
  rec: SpineRecommendation,
  selected: number,
): SpineMatrixRow['verdict'] {
  if (rec.status === 'ok') {
    if (selected >= rec.recommendedMin && selected <= rec.recommendedMax) return 'on';
    if (selected < rec.recommendedMin) return 'slightly-stiff';
    return 'slightly-weak';
  }
  return rec.status === 'stiff' ? 'too-stiff' : 'too-weak';
}

/**
 * Compute the matrix — one row per spine in SPINE_LADDER.
 *
 * Pure function. Caller memoizes on the inputs object. Returns the full
 * ladder in stiffness order so the UI can render the table without sorting.
 */
export function computeSpineMatrix(
  inputs: SpineMatrixInputs,
): SpineMatrixRow[] {
  return SPINE_LADDER.map((spine) => {
    const rec = recommendSpineFromInputs({ ...inputs, selectedSpine: spine });
    return {
      spine,
      status: rec.status,
      verdict: spineVerdictForSelection(rec, spine),
      message: rec.message,
      recommendedMin: rec.recommendedMin,
      recommendedMax: rec.recommendedMax,
      effectiveDrawWeight: rec.effectiveDrawWeight,
    };
  });
}

/**
 * True when no spine in the ladder lands in-window or borderline.
 * Triggers the "talk to a pro shop / check your inputs" helper copy on
 * extremely stiff or weak setups that the standard ladder doesn't cover.
 */
export function noStandardSpineFits(matrix: readonly SpineMatrixRow[]): boolean {
  return matrix.every((row) => row.verdict === 'too-stiff' || row.verdict === 'too-weak');
}

/**
 * Helpful-copy block surfaced when noStandardSpineFits is true. UI renders
 * this verbatim — Sal-voice, no fluff, points at the most likely causes.
 */
export const NO_STANDARD_SPINE_FITS_COPY =
  'None of the standard spines (250–1000) land in window at your current effective draw weight. This often means an extremely heavy compound or a heavily front-loaded arrow build. Talk to a pro shop about a custom spine setup, or check your draw-length / bow-type inputs above.';

/**
 * Find the spine in the ladder closest to in-window at the user's current
 * setup. Used by the dropdown to default-select something useful instead
 * of an arbitrary value. Picks the spine whose verdict is 'on' first,
 * then 'slightly-stiff' (one click stiffer than chart), then
 * 'slightly-weak' (one click weaker). Falls back to the middle of the
 * ladder if nothing qualifies.
 */
export function defaultSuggestedSpine(matrix: readonly SpineMatrixRow[]): number {
  const ranked: SpineMatrixRow['verdict'][] = ['on', 'slightly-stiff', 'slightly-weak'];
  for (const tier of ranked) {
    const hit = matrix.find((row) => row.verdict === tier);
    if (hit) return hit.spine;
  }
  return SPINE_LADDER[Math.floor(SPINE_LADDER.length / 2)];
}
